import React from 'react';
import {v4 as uuidv4} from 'uuid';
import { addClassNames } from '../../Functions/Helper/Component';
import { CallbackContextProvider } from './CallbacksContext';
import { X as CloseIcon } from "./Icons";
import useModalPortal from '../../Hooks/useModalPortal';
import { useCallbackSubmission } from '../../Hooks/useFormCallback';
import LoadingSpinner from './LoadingSpinner';
import { 
    ChevronLeft as BackIcon,
    PlusCircle as AddNew,
    FloppyFill
} from './Icons';
import { ErrorMessage } from '../ErrorMessage';
import useActionDialog from '../../Provider/ActionDialogContext';

function ModalDisplay({ 
    modal,
    modalDOM,
    titleId, 
    title,
    actionName,
    nextActionName,
    actionType = "Save",
    BodyComponent, 
    bodyProps = {},
    TitleComponent, 
    titleProps: {
        className: titleClassName,
        ...titleProps
    } = {},
    classNames: {
        modalHeaderClassName,
        modalTitleClassName,
        modalBodyClassName,
        modalFooterClassName,
        closeActionClassName,
        submitActionClassName,
        saveActionClasName,
        saveAndNextActionClassName
    },
    addNew,
    onSubmit: bodyOnSubmit,
    disableSubmit,
    ...props
}) {
    /**
     * As form is under the Body Component and the Trigger is here
     * to pass the Form Data in onSubmit call parent CallbackContext is used.
     * 
     * The setter are inserted to CallbackContext by useFormCallback hook
     * in the Components/Generic/Form components
     */
    const callbackHookedOnSubmit = useCallbackSubmission(bodyOnSubmit);
    const lastSaveRef = React.useRef(Date.now());
    // For displaying error messages where there is a submission error (server)
    const [errored, setErrored] = React.useState(null);
    const hide = React.useCallback(async(e) => {
        e.preventDefault();
        e.stopPropagation();
        modal.hide();
    }, [modal]);

    const onSave = React.useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof callbackHookedOnSubmit !== "function") {
            return false;
        }
        try {
            const result = await callbackHookedOnSubmit();
            if (result) lastSaveRef.current = Date.now();
            if (modalDOM) modalDOM.scrollTo({top:0, behavior:"smooth"});
            setTimeout(() => modal.show(), 100);
            return result;
        } catch (err) {
            setErrored(err);
            return false; 
        }
    }, [callbackHookedOnSubmit, modalDOM, modal]);
    
    const onSaveAndAdd = React.useCallback(async (e) => {
        const result = await onSave(e);
        if (!result) return false;
        if (typeof addNew === "function") setTimeout(() => addNew(result), 10);
        else modal.hide();
    }, [onSave, modal, addNew])
    return (
        <>
            <div className={addClassNames("modal-header", modalHeaderClassName)}>
                {TitleComponent? (
                    <TitleComponent 
                        id={titleId}
                        className={addClassNames("modal-title", titleClassName, modalTitleClassName)}
                        {...titleProps}
                    >{title}</TitleComponent>
                ) : (
                    <h5
                        id={titleId}
                        className={addClassNames("modal-title", titleClassName, modalTitleClassName)}
                        {...titleProps}
                    >{title}</h5>
                )}
                <button
                    type="button"
                    className="btn-close icon-button"
                    aria-label="Close"
                    data-bs-dismiss="modal"
                    onClick={hide}
                ><CloseIcon width="1em" height="1em"/></button>
            </div>
            
            <div className={addClassNames("modal-body", modalBodyClassName)}>
                <BodyComponent {...props}/>
            </div>
            
            <div className={addClassNames("modal-footer d-flex flex-row gap-2 flex-nowrap justify-content-between align-items-center", modalFooterClassName)}>
                <button 
                    type="button"
                    className={addClassNames("btn btn-outline-secondary rounded-pill", closeActionClassName )}
                    data-bs-dismiss="modal"
                    onClick={() => {
                        modal.hide(); 
                    }}
                ><BackIcon className="me-2"/> Back</button>
                <div className="justify-content-start flex-grow-1">
                    {errored && (
                        <ErrorMessage 
                            className={"m-0"}
                            error={errored}
                            action={`submitting ${title} modal form`}
                        />
                    )}
                </div>
                <div className={addClassNames("btn-group", submitActionClassName)}>
                    <button 
                        type="button"
                        className={addClassNames("btn btn-primary pe-3", saveActionClasName)}
                        onClick={onSave}
                        ref={(ref) => {;
                            if (!ref) return;
                            const lastSave = lastSaveRef.current;
                            const nextSave = lastSave + 30000;
                            if (nextSave <= Date.now()) {
                                ref.classList.add("anim-pulse");
                            } else {
                                ref.classList.remove("anim-pulse");
                                setTimeout(() => {
                                    ref.classList.add("anim-pulse");
                                }, (nextSave - Date.now()));
                            }
                        }}
                        disabled={disableSubmit}
                    >{actionName || (<span><FloppyFill clasName="me-2"/> {actionType}</span>)}</button>
                    {addNew && <button 
                        type="button"
                        className={addClassNames("btn btn-secondary", saveAndNextActionClassName)}
                        onClick={onSaveAndAdd}
                        disabled={disableSubmit}
                    >{nextActionName || (<span>{actionType} and Add New <AddNew className="ms-2"/></span>) }</button>}
                </div>
            </div>
        </>
    );
} 

function ModalDefinition({
    title, 
    actionName, 
    triggerName, 
    quitConfirmation,
    onClose,
    onClosed,
    TriggerButtonComponent, 
    triggerButtonProps: {
        className: triggerButtonClassName,
        ...triggerButtonProps
    } = {},
    classNames: {
        modalClassName, modalDialogClassName,
        modalContentClassName, modalTriggerClassName,
        ...classNames
    } = {},
    onClick: _onClick,
    loading,
    shown: _shown,
    setShown: _setShown,
    ...props
}) {
    const titleId = React.useMemo(() => `${uuidv4()}-label`, []);
    const [cachedShown, cacheShown] = React.useState(_shown);
    const { confirm } = useActionDialog();
    const { uuid, modal, modalDOM, ModalPortal } = useModalPortal();
    const modalClosureRef = React.useRef(false);
    
    const shown = React.useMemo(() => {
        if (typeof _shown !== cachedShown && _shown) modal.show();
        
        return (typeof _shown === "boolean"? _shown : cachedShown)
    }, [_shown, cachedShown, modal]);
    const setShown = React.useCallback((newData) => {
        if (typeof _setShown === "function") _setShown(newData); // It should sync with the useEffect above
        else cacheShown(newData);
    }, [_setShown]);
    const onClick = React.useCallback((e) => {
        if (typeof _onClick === "function") _onClick(e);
        modal.show();
        setShown(true);
    }, [modal, _onClick, setShown]);
    React.useEffect(() => {
        if (shown && modalDOM) {
            const _onClose = (e) => {
                if (quitConfirmation &&  !modalClosureRef.current) {
                    e.preventDefault();
                    
                    confirm(
                        typeof quitConfirmation === "string"? quitConfirmation :
                        <>Are you sure you want to exit? Unsaved changes will be lost.
                        please save by clicking on <span className="bg-primary text-light py-1 px-2 me-1 rounded">{actionName || (<span><FloppyFill clasName="me-2"/> {props.actionType}</span>)}</span> button</>
                    ).then(result => {
                        if (result) {
                            modalClosureRef.current = true;
                            modal.hide();
                        } else {
                            modal.show();
                            modalDOM.scrollTo({ top: modalDOM.scrollHeight, behavior: "smooth"});
                        }
                    });
                } else {
                    modalClosureRef.current = false;
                    if (typeof onClose === 'function') onClose(e);
                }
            };
            const _onClosed = (e) => {
                setShown(false);
                if (typeof onClosed === 'function') onClosed(e);
            };
            
            modalDOM.addEventListener('hide.bs.modal', _onClose);
            modalDOM.addEventListener('hidden.bs.modal', _onClosed);

            return () => {
                modalDOM.removeEventListener('hide.bs.modal', _onClose);
                modalDOM.removeEventListener('hidden.bs.modal', _onClosed);
            };
        }
    }, [uuid, modal, modalDOM, shown, setShown, onClose, onClosed, confirm, quitConfirmation, actionName, props.actionType]);

    return (
        <>
            {shown && (
                <ModalPortal 
                    modalType={addClassNames("modal-lg", modalClassName)}
                    aria-labelledby={titleId}
                    modalDialogClassName={modalDialogClassName}
                    modalContentClassName={modalContentClassName}
                >
                    {shown && (loading? 
                        <div clasName="p-5"><LoadingSpinner/> </div>: 
                        <ModalDisplay
                            titleId={titleId} 
                            title={title}
                            actionName={actionName} 
                            classNames={classNames}
                            modal={modal}
                            modalDOM={modalDOM}
                            {...props}
                        />
                    )}
                </ModalPortal>
            )}
            {TriggerButtonComponent? (
                <TriggerButtonComponent
                    className={addClassNames("btn", triggerButtonClassName, modalTriggerClassName)}
                    onClick={onClick}
                    {...triggerButtonProps}
                />
            ) :  (
                <button
                    type="button"
                    className={addClassNames("btn", triggerButtonClassName, modalTriggerClassName)}
                    onClick={onClick}
                    {...triggerButtonProps}
                >{triggerName || actionName || addClassNames("Open", title)}</button>
            
            )}
        </>
        
    );
}

export default function FormModal(props) {
    return (
        <CallbackContextProvider>
            <ModalDefinition {...props}/>
        </CallbackContextProvider>
    );
}